import React from "react";
import { useSelector } from "react-redux";
import { kRoute } from "src/kRoute";
import { useAppDispatch } from "src/redux";
import { ActivityIndicator, Image, Pressable, StyleSheet, TouchableOpacity, View } from "react-native";
import { NavigationProp, ParamListBase, useIsFocused, useNavigation } from "@react-navigation/native";

import { tBrandRepConnectionResponse, tVisualConnectionStatus } from "tmt_api/src/types/types.validator";

import * as utilsDb from "src/db/utils.db";
import * as connectionDb from "src/db/connection.db";
import * as mappers from "tmt_api/src/shared/mappers";
import { PdCopy, PdSettings } from "src/components/Icons";
import { PdStack, PdText } from "src/components/layout";
import {
  PdBadge,
  PdBadgeLabel,
  PdTable,
  PdTableCell,
  PdTableCellText,
  PdTablePaginationFooter,
  PdTableRow,
  PdTableRowMobile,
  PdTableSortableHeaders,
} from "src/components/v2/layout";
import { tPressableState } from "src/components/types";
import { PdSearchInput } from "src/components/v2/ui";
import { tColumn, tRowInstance, useTable } from "src/hooks/table";
import { useDebounce, useIsMobile } from "src/hooks/ui";
import { generateCSVFromTableInstance, getHiddenColumnsFromConfig } from "src/util/table";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";

import { kTablePageSize } from "src/util/constants";
import { changeFilterNextPage, changeFilterPrevPage, changeFilterTerm, resetFilters } from "src/redux/filters/actions";
import { AlertModal, tAlertModalProps } from "src/components/Modals/AlertModal";
import { useModal } from "src/providers/OverlayProvider";
import { PdFormLabel } from "src/components/v2/forms/control";
import { PdButton, PdButtonLabel } from "src/components/v2/forms/button";
import { PdPopover, PdPopoverContent, PdPopoverTrigger } from "src/components/v2/overlay";
import PopoverContentTableColumns from "src/components/connected/PopoverContentTableColumns";
import { PdTableMobileItemSeparator, tPdBadgeColorScheme } from "src/components/v2/layout";

type tTableState = {
  data: tBrandRepConnectionResponse[];
  isLoading: boolean;
  prev: string | null;
  next: string | null;
};

const formatDate = (dateInMilliseconds: number) => new Date(dateInMilliseconds).toLocaleDateString("en-US");

const getColorSchemeFromConnectionStatus = (status: tVisualConnectionStatus): tPdBadgeColorScheme => {
  if (status === "Declined") return "red";
  if (status === "Pending") return "yellow";
  if (status === "Invited") return "orange";
  return "green";
};

const getLabelFromConnection = (data: tBrandRepConnectionResponse): string => {
  if (data.source === "rep" && data.status === "Invited") return "Rep Invite";
  return data.status;
};

const Row: React.FC<{
  item: tRowInstance<tBrandRepConnectionResponse>;
  onPress?: (item: tRowInstance<tBrandRepConnectionResponse>) => void;
}> = ({ item, onPress }) => {
  const handleOnPress = () => {
    onPress?.(item);
  };

  return (
    <Pressable onPress={handleOnPress}>
      {({ pressed, hovered }: tPressableState) => (
        <PdTableRow style={[hovered ? styles.tableRowHovered : {}, pressed ? styles.tableRowPressed : {}]}>
          {item.cells.map((cell) => (
            <PdTableCell key={cell.uniqueId} style={cell.style}>
              {cell.render("Cell")}
            </PdTableCell>
          ))}
        </PdTableRow>
      )}
    </Pressable>
  );
};

const MobileRow: React.FC<{
  item: tRowInstance<tBrandRepConnectionResponse>;
  onPress?: (item: tRowInstance<tBrandRepConnectionResponse>) => void;
}> = ({ item, onPress }) => {
  const navigation = useNavigation();

  const handleOnPress = () => {
    onPress?.(item);
  };

  return (
    <TouchableOpacity onPress={handleOnPress}>
      <PdTableRowMobile style={styles.mobileRow}>
        <TouchableOpacity onPress={() => navigation.navigate(kRoute.P20, { id: item.repId, connectionId: item.id })}>
          <Image style={styles.profile} source={{ uri: item.repProfileUrl }} />
        </TouchableOpacity>
        <View style={styles.flex1}>
          <PdText>{item.repName}</PdText>
          <PdText>{item.repEmail}</PdText>
        </View>
        <PdBadge colorScheme={getColorSchemeFromConnectionStatus(item.status)}>
          <PdBadgeLabel>{item.status}</PdBadgeLabel>
        </PdBadge>
      </PdTableRowMobile>
    </TouchableOpacity>
  );
};

const P12_ViewSalesReps: React.FC<{ navigation: NavigationProp<ParamListBase> }> = ({ navigation }) => {
  const dispatch = useAppDispatch();
  const rCompanyId = useSelector((state) => state.UserReducer.profile.companyRefPath.split("/")?.[1]);
  const rTableConfigs = useSelector((state) => state.TableConfigReducer.applications);
  const filters = useSelector((state) => state.FiltersReducer.salesReps);
  const [tableState, setTableState] = React.useState<tTableState>({
    isLoading: true,
    data: [],
    prev: null,
    next: null,
  });
  const isFocused = useIsFocused();
  const dTermFilter = useDebounce(filters.term, 500);
  const alertModal = useModal<tAlertModalProps>(AlertModal);
  const columns: tColumn<tBrandRepConnectionResponse>[] = React.useMemo(
    () => [
      {
        id: "avatar",
        header: () => null,
        accessor: (originalRow) => originalRow.id,
        Cell: ({ row }) => (
          <TouchableOpacity onPress={() => navigation.navigate(kRoute.P20, { id: row.repId, connectionId: row.id })}>
            <Image style={styles.profile} source={{ uri: row.repProfileUrl }} />
          </TouchableOpacity>
        ),
        isSortable: false,
        style: styles.tableColumnXs,
      },
      {
        id: "repName",
        header: "Rep",
        accessor: (originalRow) => originalRow.repName,
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.flex2,
      },
      {
        id: "repEmail",
        header: "Email",
        accessor: (originalRow) => originalRow.repEmail,
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.flex2,
      },
      {
        id: "status",
        header: "Status",
        accessor: (originalRow) => getLabelFromConnection(originalRow),
        Cell: ({ value, row }) => (
          <PdBadge colorScheme={getColorSchemeFromConnectionStatus(row.status)}>
            <PdBadgeLabel>{value}</PdBadgeLabel>
          </PdBadge>
        ),
        style: styles.flex1,
      },
      {
        id: "connectionDate",
        header: "Connection Date",
        accessor: (originalRow) => formatDate(originalRow.createdAtInMillis),
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        containerStyle: styles.flex1,
        style: styles.flex1,
      },
    ],
    [navigation],
  );
  const tableInstance = useTable<tBrandRepConnectionResponse>({
    data: tableState.data,
    columns,
    initialState: {
      sortedBy: "createdAtInMillis",
      sortDirection: "desc",
      hiddenColumns: getHiddenColumnsFromConfig(rTableConfigs),
    },
  });
  const isMobile = useIsMobile();

  const handleOnPressCopyAsCSV = async () => {
    try {
      await navigator.clipboard.writeText(generateCSVFromTableInstance(tableInstance.rows, tableInstance.headers));
      dispatch({ type: "RIBBON", message: "Copied to clipboard" });
    } catch (er) {
      dispatch({ type: "RIBBON", message: new Error("Could not copy to clipboard") });
    }
  };

  const updateHiddenColumns = (newTableConfig: Record<keyof tBrandRepConnectionResponse, boolean>) => {
    const newHiddenColumns = getHiddenColumnsFromConfig(newTableConfig);
    tableInstance.setHiddenColumns(newHiddenColumns);
  };

  const handleOnPressRow = React.useCallback(
    (item: tRowInstance<tBrandRepConnectionResponse>) => {
      if (item.status === "Active") navigation.navigate(kRoute.P20, { id: item.repId, connectionId: item.id });
      else
        alertModal.openAsync({
          title: `Connect with ${item.repName}`,
          description: () => (
            <>
              <PdText
                style={styles.mb4}
              >{`${item.repName} is now using The Main Tab for wholesale and is inviting you to work with them on our marketplace. You can now assign ${item.repName} & their designated commission to any of the active retailers you are connected with on the Main Tab. Simply navigate to your Customers, click on a Retailer you'd like to assign ${item.repName} to and enter the ${item.repName} e-mail address ${item.repEmail} and the commission you'd like them to earn per order this Retailer will place.`}</PdText>
              <PdText
                style={styles.mb10}
              >{`${item.repName} will then be notified once a Retailer is assigned to them & can begin to communicate and manage the relationship! ${item.repName} will be paid the commission you assign per retailer once you mark an order shipped. You can remove a rep from a retailer at any time.`}</PdText>
            </>
          ),
          maxWidth: 600,
        });
    },
    [alertModal, navigation],
  );

  const handleOnPressPrevPage = () => {
    dispatch(changeFilterPrevPage({ pageName: "salesReps", value: tableState?.prev ?? "" }));
  };

  const handleOnPressNextPage = () => {
    dispatch(changeFilterNextPage({ pageName: "salesReps", value: tableState?.next ?? "" }));
  };

  const renderItem: React.FC<{ item: tRowInstance<tBrandRepConnectionResponse> }> = React.useCallback(
    ({ item }) => {
      if (isMobile) return <MobileRow item={item} onPress={handleOnPressRow} />;
      return <Row item={item} onPress={handleOnPressRow} />;
    },
    [handleOnPressRow, isMobile],
  );

  React.useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    (async () => {
      const query = await utilsDb.withPagination(
        connectionDb.queryBrandRepConnection,
        connectionDb.documentBrandRepConnection,
        {
          term: dTermFilter,
          brandId: rCompanyId,
          prev: filters.prev,
          next: filters.next,
          limit: kTablePageSize,
        },
      );

      unsubscribe = query.onSnapshot((snapshot) => {
        const data = snapshot.docs.map((doc) => mappers.mapBrandRepConnection(doc.data()));
        setTableState({
          isLoading: false,
          data,
          prev: data[0]?.id ?? "",
          next: data.length >= kTablePageSize ? data[data.length - 1]?.id ?? "" : "",
        });
      });
    })();

    return () => {
      unsubscribe?.();
    };
  }, [dTermFilter, filters.next, filters.prev, rCompanyId]);

  React.useEffect(() => {
    if (!isFocused) {
      dispatch(resetFilters());
    }
  }, [dispatch, isFocused]);

  if (tableState.isLoading) {
    return (
      <View style={[styles.flex1, styles.alignCenter, styles.justifyCenter]}>
        <ActivityIndicator size="large" color={kColors.brandGreen} />
      </View>
    );
  }

  return (
    <View style={isMobile ? styles.mobilePage : styles.page}>
      <PdText variant="subheader" style={styles.mb4}>
        Sales Reps
      </PdText>
      <View style={styles.mb4}>
        {!isMobile && <PdFormLabel>Search your sales reps</PdFormLabel>}
        <PdSearchInput
          value={filters.term}
          onChangeText={(value) => dispatch(changeFilterTerm({ pageName: "salesReps", value }))}
        />
      </View>
      {!isMobile && (
        <PdStack style={styles.tableOptionsContainer}>
          <PdButton
            colorScheme="gray"
            size="sm"
            rightIcon={<PdCopy style={styles.iconSm} />}
            onPress={handleOnPressCopyAsCSV}
          >
            <PdButtonLabel>Copy as CSV</PdButtonLabel>
          </PdButton>
          <PdPopover offset={22 + 2 * kStyleConst.kSpacing}>
            <PdPopoverTrigger>
              <PdButton colorScheme="gray" size="sm" rightIcon={<PdSettings style={styles.iconSm} />}>
                <PdButtonLabel>Configure Table</PdButtonLabel>
              </PdButton>
            </PdPopoverTrigger>
            <PdPopoverContent style={styles.configureTablePopoverContent}>
              <PopoverContentTableColumns
                columns={columns}
                tableConfigReduxKey="salesReps"
                onPressCheckbox={updateHiddenColumns}
              />
            </PdPopoverContent>
          </PdPopover>
        </PdStack>
      )}
      <PdTable
        ListHeaderComponent={!isMobile ? <PdTableSortableHeaders headers={tableInstance.headers} /> : null}
        ItemSeparatorComponent={() => (isMobile ? <PdTableMobileItemSeparator /> : null)}
        data={tableInstance.rows}
        keyExtractor={(item) => item.id}
        renderItem={renderItem}
        contentContainerStyle={styles.mrReset}
        isMobile={isMobile}
      />
      <PdTablePaginationFooter
        page={filters.page}
        hasNext={!!tableState?.next}
        onPressBack={handleOnPressPrevPage}
        onPressNext={handleOnPressNextPage}
      />
    </View>
  );
};

export const P12 = React.memo(P12_ViewSalesReps);

const styles = StyleSheet.create({
  alignCenter: {
    alignItems: "center",
  },
  configureTablePopoverContent: {
    backgroundColor: kColors.white,
    borderColor: kColors.gray300,
    borderRadius: 5,
    borderWidth: 1,
    left: -150,
    paddingHorizontal: kStyleConst.kSpacing * 4,
    paddingVertical: kStyleConst.kSpacing * 3,
    right: 0,
  },
  flex1: {
    flex: 1,
  },
  flex2: {
    flex: 2,
  },
  iconSm: {
    height: 11,
    width: 11,
  },
  justifyCenter: {
    justifyContent: "center",
  },
  mb10: {
    marginBottom: 10 * kStyleConst.kSpacing,
  },
  mb4: {
    marginBottom: kStyleConst.kSpacing * 4,
  },
  mobilePage: {
    flex: 1,
    paddingBottom: kStyleConst.kSpacing * 2,
    paddingHorizontal: kStyleConst.kSpacing * 4,
    paddingTop: kStyleConst.kSpacing * 4,
  },
  mobileRow: {
    alignItems: "center",
    flexDirection: "row",
    padding: kStyleConst.kSpacing * 2,
  },
  mrReset: {
    marginRight: "initial",
  },
  page: {
    flex: 1,
    paddingBottom: kStyleConst.kSpacing * 4,
    paddingHorizontal: kStyleConst.kSpacing * 10,
    paddingTop: kStyleConst.kSpacing * 8,
  },
  profile: {
    backgroundColor: kColors.lightMed,
    borderRadius: kStyleConst.kTableProfileImageDimension / 2,
    height: kStyleConst.kTableProfileImageDimension,
    marginEnd: kStyleConst.kSpacing * 2,
    width: kStyleConst.kTableProfileImageDimension,
  },
  tableColumnXs: {
    width: 50,
  },
  tableOptionsContainer: {
    marginBottom: kStyleConst.kSpacing * 2,
    marginLeft: "auto",
    zIndex: 1,
  },
  tableRowHovered: {
    backgroundColor: kColors.gray50,
  },
  tableRowPressed: {
    opacity: 0.6,
  },
});
